import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  getDocs,
  increment,
  serverTimestamp,
  query,
  orderBy,
  limit
} from 'firebase/firestore';
import app from '../firebase';
import { CampaignType, FactionType } from '../types';

interface GameStatistics {
  campaign: CampaignType;
  playerFaction: FactionType;
  opponentFaction: FactionType;
  gameMode: 'PvP' | 'PvE' | 'Online';
  timestamp: any;
  duration?: number;
  turnsPlayed?: number;
  winner?: FactionType;
}

interface PopularityStats {
  campaigns: Map<string, number>;
  factions: Map<string, number>;
  gameModes: Map<string, number>;
  totalGames: number;
}

class StatisticsService {
  private static instance: StatisticsService | null = null;
  private db;
  
  private constructor() {
    this.db = getFirestore(app);
  }
  
  public static getInstance(): StatisticsService {
    if (!StatisticsService.instance) {
      StatisticsService.instance = new StatisticsService();
    }
    return StatisticsService.instance;
  }
  
  // Track when a game starts
  public async trackGameStart(data: {
    campaign: CampaignType;
    playerFaction: FactionType;
    opponentFaction: FactionType;
    gameMode: 'PvP' | 'PvE' | 'Online';
  }): Promise<void> {
    try {
      // Update global statistics
      const statsRef = doc(this.db, 'statistics', 'global');
      await setDoc(statsRef, {
        totalGames: increment(1),
        [`campaigns.${data.campaign}`]: increment(1),
        [`factions.${data.playerFaction}`]: increment(1),
        [`gameModes.${data.gameMode}`]: increment(1),
        lastUpdated: serverTimestamp()
      }, { merge: true });
      
      // Store individual game record
      const gameRef = doc(collection(this.db, 'games'));
      await setDoc(gameRef, {
        ...data,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.warn('Failed to track game start:', error);
    }
  }
  
  // Track when a game ends
  public async trackGameEnd(data: {
    winner: FactionType;
    duration: number;
    turnsPlayed: number;
  }): Promise<void> {
    try {
      // Update faction win counts
      const statsRef = doc(this.db, 'statistics', 'global');
      await setDoc(statsRef, {
        [`factionWins.${data.winner}`]: increment(1),
        totalTurnsPlayed: increment(data.turnsPlayed),
        totalDuration: increment(data.duration),
        lastUpdated: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.warn('Failed to track game end:', error);
    }
  }
  
  // Get popularity statistics
  public async getPopularityStats(): Promise<PopularityStats | null> {
    try {
      console.log('Fetching popularity stats from Firestore...');
      const statsRef = doc(this.db, 'statistics', 'global');
      const statsSnap = await getDoc(statsRef);
      
      if (!statsSnap.exists()) {
        console.log('No statistics document exists yet. Creating default stats...');
        // Return default empty stats if no data exists yet
        return {
          campaigns: new Map(),
          factions: new Map(),
          gameModes: new Map(),
          totalGames: 0
        };
      }
      
      const data = statsSnap.data();
      console.log('Statistics data retrieved:', data);
      
      return {
        campaigns: new Map(Object.entries(data.campaigns || {})),
        factions: new Map(Object.entries(data.factions || {})),
        gameModes: new Map(Object.entries(data.gameModes || {})),
        totalGames: data.totalGames || 0
      };
    } catch (error) {
      console.error('Failed to get popularity stats:', error);
      // Return empty stats instead of null to prevent loading forever
      return {
        campaigns: new Map(),
        factions: new Map(), 
        gameModes: new Map(),
        totalGames: 0
      };
    }
  }
  
  // Get recent games
  public async getRecentGames(limitCount: number = 10): Promise<GameStatistics[]> {
    try {
      const gamesRef = collection(this.db, 'games');
      const q = query(gamesRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const querySnapshot = await getDocs(q);
      
      const games: GameStatistics[] = [];
      querySnapshot.forEach((doc) => {
        games.push(doc.data() as GameStatistics);
      });
      
      return games;
    } catch (error) {
      console.warn('Failed to get recent games:', error);
      return [];
    }
  }
  
  // Get faction win rates
  public async getFactionWinRates(): Promise<Map<string, number> | null> {
    try {
      console.log('Fetching faction win rates...');
      const statsRef = doc(this.db, 'statistics', 'global');
      const statsSnap = await getDoc(statsRef);
      
      if (!statsSnap.exists()) {
        console.log('No statistics for win rates yet');
        return new Map();
      }
      
      const data = statsSnap.data();
      const factionWins = data.factionWins || {};
      const factionGames = data.factions || {};
      
      const winRates = new Map<string, number>();
      
      for (const faction in factionGames) {
        const wins = factionWins[faction] || 0;
        const games = factionGames[faction] || 1;
        const winRate = (wins / games) * 100;
        winRates.set(faction, Math.round(winRate));
      }
      
      return winRates;
    } catch (error) {
      console.error('Failed to get faction win rates:', error);
      return new Map();
    }
  }
}

export default StatisticsService;